import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { IdField } from '~/components/Fields';
import {
    LabelText,
    ValueText,
    WrappingValueText,
} from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { useIsInObjectDetails } from './objectDetailsContext';
import PropertiesPanel, { PropertiesVariant } from './PropertiesPanel';

interface RegistrationPropertiesProps {
    registration: RigidRegistration;
    /** Opens the registration's DICOM tags; a hover panel's route to them. */
    onViewDicom?: () => void;
    onDelete?: () => void;
    /** The layout of the list; `compact` by default. */
    variant?: PropertiesVariant;
}

export default function RegistrationProperties({
    registration,
    onViewDicom,
    onDelete,
    variant,
}: RegistrationPropertiesProps) {
    const intl = useIntl();
    const inDetailsDialog = useIsInObjectDetails();
    const dash = '\u2014';

    return (
        <PropertiesPanel
            variant={variant}
            onViewDicom={onViewDicom}
            onDelete={onDelete}
        >
            <LabelText>
                <FormattedMessage id="label" />
            </LabelText>
            <WrappingValueText component="span">
                <SensitiveInfoTypography>
                    {registration.contentLabel || dash}
                </SensitiveInfoTypography>
            </WrappingValueText>

            <LabelText>
                <FormattedMessage id="registration.contentDescription" />
            </LabelText>
            <WrappingValueText component="span">
                <SensitiveInfoTypography>
                    {registration.contentDescription || dash}
                </SensitiveInfoTypography>
            </WrappingValueText>

            <LabelText>
                <FormattedMessage id="registration.contentCreator" />
            </LabelText>
            <WrappingValueText component="span">
                <SensitiveInfoTypography>
                    {registration.contentCreatorName || dash}
                </SensitiveInfoTypography>
            </WrappingValueText>

            <LabelText>
                <FormattedMessage id="registration.contentDate" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>
                    {registration.contentDate || dash}
                </SensitiveInfoTypography>
            </ValueText>

            {/* The dialog states it in its header, a hover panel has no header. */}
            {!inDetailsDialog && (
                <>
                    <LabelText>
                        <FormattedMessage id="approvalStatus" />
                    </LabelText>
                    <ValueText component="div">
                        <ReviewStatus
                            status={registration.approvalStatus}
                            fontSize={12}
                            fontWeight={500}
                        />
                    </ValueText>
                </>
            )}

            <LabelText>
                {intl.formatMessage({ id: 'registration.sourceImage' })} FOR
            </LabelText>
            <IdField
                id={registration.sourceFrameOfReferenceUid}
                typographyProps={{ fontSize: 11 }}
            />

            <LabelText>
                {intl.formatMessage({ id: 'registration.targetImage' })} FOR
            </LabelText>
            <IdField
                id={registration.targetFrameOfReferenceUid}
                typographyProps={{ fontSize: 11 }}
            />

            <LabelText>
                {intl.formatMessage({ id: 'sopInstanceUid' })}
            </LabelText>
            <IdField
                id={registration.sopInstanceUid}
                typographyProps={{ fontSize: 11 }}
            />
        </PropertiesPanel>
    );
}
